/**
 * The signed-in user's own files (backend routers/files.py). Each user only ever sees their workspace's files.
 *   GET    workspace/files                 {files: [{id, filename, mime, size_bytes, status, error, pages, chunks}]}
 *   POST   workspace/files                 multipart "file"; status starts as 'processing'
 *   GET    workspace/files/{id}/content    the original file
 *   DELETE workspace/files/{id}
 */
import axios from 'axios';
import { apiUrl } from './config';

const userEmail = () => (typeof localStorage !== 'undefined' ? localStorage.getItem('userinfo') : null);
const params = () => ({ userEmail: userEmail() });

export const ACCEPT = '.pdf,.docx,.txt,.md,.csv,.json,.log,.xml,.html,.tsv,.yaml,.yml,.png,.jpg,.jpeg';

export async function listFiles() {
  const res = await axios.get(apiUrl('workspace/files'), { params: params() });
  return res.data;
}

export async function uploadFile(file, onProgress) {
  const body = new FormData();
  body.append('file', file);
  const res = await axios.post(apiUrl('workspace/files'), body, {
    params: params(),
    onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((100 * e.loaded) / e.total)),
  });
  return res.data;
}

export async function deleteFile(id) {
  await axios.delete(apiUrl(`workspace/files/${encodeURIComponent(id)}`), { params: params() });
}

/** URL that opens a file: `workspace/files/<id>/content[#page=N]` (a source's file_path) or a file id. */
export function fileUrl(pathOrId) {
  const raw = String(pathOrId || '');
  const path = raw.startsWith('workspace/') ? raw : `workspace/files/${encodeURIComponent(raw)}/content`;
  const [base, hash] = path.split('#');
  const email = userEmail();
  const query = process.env.NODE_ENV === 'development' && email ? `?userEmail=${encodeURIComponent(email)}` : '';
  return `${apiUrl(base)}${query}${hash ? `#${hash}` : ''}`;
}

/** Poll until every file in `ids` has left 'processing' (or the time runs out). */
export async function waitIndexed(ids, { interval = 1500, timeout = 120000, onUpdate } = {}) {
  const started = Date.now();
  for (;;) {
    const { files } = await listFiles();
    const mine = files.filter((f) => ids.includes(f.id));
    if (onUpdate) onUpdate(files);
    if (!mine.some((f) => f.status === 'processing') || Date.now() - started > timeout) return mine;
    await new Promise((r) => setTimeout(r, interval));
  }
}

export const formatSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
